// Mémorise la dernière réponse réussie de l'API pour chaque contenu du site.
//
// Pourquoi : le secours servait jusqu'ici un JSON figé à la compilation, que
// le déploiement rafraîchissait en lisant la base de production. Ce couplage
// interdisait toute construction ailleurs que sur le serveur — donc toute
// livraison continue. En mémorisant côté navigateur, le secours devient frais
// par visiteuse au lieu de frais par déploiement, et le build ne lit plus
// jamais la base.
//
// Les JSON de `src/api/fallback/` restent : ils amorcent la toute première
// visite, celle d'une personne qui arrive pendant une panne sans avoir jamais
// vu le site.

const PREFIXE = 'azoya:contenu:'

// À incrémenter dès qu'une charge utile de `/api/public/*` change de forme.
// Une entrée d'une autre version est ignorée plutôt que servie : mieux vaut
// retomber sur la copie embarquée que rendre une page avec des champs
// manquants.
// Reste à 1 après l'ajout de l'`encart` aux familles du catalogue (#164), et
// c'est un choix : cette version est GLOBALE alors que les clés sont par
// chemin. L'incrémenter n'invalide pas le seul catalogue — elle jette aussi
// practical-info, trust-bar, home-visuals, testimonials, offers,
// booking-settings et les deux pages légales, dont aucune n'a changé de forme.
// Les pages légales n'ont même pas de copie embarquée : une visiteuse revenue
// pendant une panne d'API y lirait « Contenu momentanément indisponible » à la
// place des CGU qu'elle avait déjà chargées. C'est exactement la panne contre
// laquelle ce cache a été construit.
//
// Un champ facultatif ajouté ne rend rien illisible : une entrée d'avant
// l'encart se comporte comme `encart: null`, et la copie embarquée qui la
// remplacerait n'en porte pas davantage tout en étant plus ancienne. La règle
// se lit donc « change de forme » au sens fort : un champ renommé, disparu, ou
// dont la structure change.
//
// Si le jour où le panneau rendra l'encart une invalidation devient
// souhaitable, elle devra être portée PAR CHEMIN — une table
// `{ catalog: 2 }` consultée à la lecture comme à l'écriture — et non par ce
// compteur unique.
export const VERSION_CACHE = 1

// Pas de péremption volontaire. Une entrée du cache vient forcément d'une
// réponse de l'API postérieure à la compilation qui a produit la copie
// embarquée : elle est donc, sauf cas tordu, au moins aussi fraîche que
// celle-ci. Une péremption ferait retomber sur *plus vieux*, jamais sur plus
// récent. `at` est écrit quand même — pour le diagnostic, et parce qu'une
// future règle de péremption en aurait besoin.

function cle(chemin) {
  return `${PREFIXE}${chemin}`
}

export function lireContenuEnCache(chemin) {
  try {
    const brut = window.localStorage.getItem(cle(chemin))
    if (!brut) return undefined
    const entree = JSON.parse(brut)
    if (entree?.v !== VERSION_CACHE) return undefined
    return entree.data
  } catch {
    // Stockage indisponible (navigation privée, réglage du navigateur) ou
    // contenu illisible : le site fonctionne sans mémoire, ce qui est
    // exactement son comportement d'avant ce module.
    return undefined
  }
}

export function ecrireContenuEnCache(chemin, donnee) {
  try {
    const entree = { v: VERSION_CACHE, at: new Date().toISOString(), data: donnee }
    window.localStorage.setItem(cle(chemin), JSON.stringify(entree))
  } catch {
    // Quota atteint ou stockage refusé. Ne rien faire : perdre la mémoire
    // n'est pas une panne, et interrompre l'affichage pour ça en serait une.
  }
}
